import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';
import { DatePicker } from '../../components/ui/date-picker';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { fieldVisible } from '../../lib/formLogic';
import {
  ArrowLeft,
  Send,
  Loader2,
  Camera,
  X,
  AlertCircle,
  Trash2,
  Home,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const MobileFormSubmission = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, operator } = useMobileAuth();
  
  const templateId = searchParams.get('templateId');
  const jobId = searchParams.get('jobId');
  const assetId = searchParams.get('assetId');
  
  const [template, setTemplate] = useState(null);
  const [job, setJob] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (id) => setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));

  // Auto-expand any collapsed section that contains a field with a validation error.
  useEffect(() => {
    if (!template?.fields || Object.keys(errors).length === 0) return;
    let curSection = null;
    const expand = {};
    template.fields.forEach(f => {
      const ft = f.field_type || f.type;
      if (ft === 'section' || ft === 'section_header') { curSection = f.id; }
      else if (curSection && errors[f.id]) { expand[curSection] = false; }
    });
    if (Object.keys(expand).length > 0) {
      setCollapsedSections(prev => ({ ...prev, ...expand }));
    }
  }, [errors, template]);
  
  const signatureRefs = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      if (!templateId) {
        toast.error('No form template specified');
        navigate('/mobile/forms');
        return;
      }

      try {
        setLoading(true);
        
        // Fetch template
        const templateRes = await api.get(`/api/form-templates/${templateId}`);
        setTemplate(templateRes.data);
        
        // Initialize responses with default values
        const initialResponses = {};
        templateRes.data.fields?.forEach(field => {
          const fieldType = field.field_type || field.type;
          if (fieldType === 'checkbox') {
            initialResponses[field.id] = false;
          } else if (fieldType === 'multi_photo' || fieldType === 'multi_select' || fieldType === 'repeating_group') {
            initialResponses[field.id] = [];
          } else if (fieldType === 'section' || fieldType === 'section_header') {
            // structural — no response value
          } else {
            initialResponses[field.id] = '';
          }
        });
        setResponses(initialResponses);
        
        // Fetch job if job-linked
        if (jobId) {
          const jobRes = await api.get(`/api/jobs/${jobId}`);
          setJob(jobRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch form data:', error);
        toast.error('Failed to load form');
        navigate('/mobile/forms');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, templateId, jobId, navigate]);

  const handleResponseChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when field is modified
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleFieldPhotoCapture = async (fieldId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/form-submissions/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleResponseChange(fieldId, res.data.url);
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleMultiPhotoCapture = async (fieldId, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/api/form-submissions/upload-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setResponses(prev => ({ ...prev, [fieldId]: [...(prev[fieldId] || []), res.data.url] }));
      } catch (error) {
        toast.error('Failed to upload a photo');
      }
    }
    setErrors(prev => ({ ...prev, [fieldId]: null }));
    toast.success('Photo(s) added');
  };

  const removeMultiPhoto = (fieldId, index) => {
    setResponses(prev => ({ ...prev, [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index) }));
  };

  const toggleMultiSelect = (fieldId, option) => {
    setResponses(prev => {
      const cur = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const next = cur.includes(option) ? cur.filter(o => o !== option) : [...cur, option];
      return { ...prev, [fieldId]: next };
    });
    if (errors[fieldId]) setErrors(prev => ({ ...prev, [fieldId]: null }));
  };

  const addRepeatingRow = (fieldId, columns) => {
    const row = {};
    (columns || []).forEach(c => { row[c.id] = ''; });
    setResponses(prev => ({ ...prev, [fieldId]: [...(Array.isArray(prev[fieldId]) ? prev[fieldId] : []), row] }));
    if (errors[fieldId]) setErrors(prev => ({ ...prev, [fieldId]: null }));
  };
  const updateRepeatingRow = (fieldId, rowIdx, colId, value) => {
    setResponses(prev => {
      const rows = [...(prev[fieldId] || [])];
      rows[rowIdx] = { ...rows[rowIdx], [colId]: value };
      return { ...prev, [fieldId]: rows };
    });
  };
  const removeRepeatingRow = (fieldId, rowIdx) => {
    setResponses(prev => ({ ...prev, [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== rowIdx) }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    template?.fields?.forEach(field => {
      const fieldType = field.field_type || field.type;
      // Skip validation for fields hidden by conditional logic (Phase 3)
      if (!fieldVisible(field, responses)) return;
      if (field.required) {
        const value = responses[field.id];
        const empty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
        if (empty) {
          newErrors[field.id] = 'This field is required';
        } else if (fieldType === 'repeating_group' && Array.isArray(value)) {
          // A required table must have at least one row with a filled cell.
          const anyFilled = value.some(row => Object.values(row || {}).some(v => v !== '' && v !== null && v !== undefined));
          if (!anyFilled) newErrors[field.id] = 'Add at least one completed row';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        template_id: templateId,
        job_id: jobId || null,
        asset_id: assetId || null,
        data: responses
      };

      const res = await api.post('/api/form-submissions', submissionData);

      if (res?.data?.requires_approval && res?.data?.approval_status === 'pending') {
        toast.success('Form submitted — awaiting supervisor approval before work can proceed.', { duration: 6000 });
      } else {
        toast.success('Form submitted successfully!');
      }
      if (assetId) {
        navigate(`/mobile/equipment/${assetId}`);
      } else {
        navigate('/mobile/forms?tab=submitted');
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const hasError = errors[field.id];
    const fieldType = field.field_type || field.type;
    
    switch (fieldType) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              className={`bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              rows={3}
              className={`bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="number"
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              className={`bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={responses[field.id] || ''}
              onValueChange={(value) => handleResponseChange(field.id, value)}
            >
              <SelectTrigger className={`bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option, idx) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'section_header':
        return (
          <button
            type="button"
            key={field.id}
            onClick={() => toggleSection(field.id)}
            className="w-full text-left pt-5 pb-1 border-t-2 border-[#ffd108]/50 flex items-start gap-2"
            data-testid={`form-section-header-${field.id}`}
          >
            {collapsedSections[field.id] ? <ChevronRight className="h-5 w-5 text-[#ffd108] mt-0.5 shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#ffd108] mt-0.5 shrink-0" />}
            <span className="flex-1">
              <span className="block text-[#ffd108] text-lg font-bold">{field.label}</span>
              {field.description && <span className="block text-[var(--m-text-secondary)] text-sm whitespace-pre-line">{field.description}</span>}
            </span>
          </button>
        );

      case 'section':
        return (
          <div key={field.id} className="pt-4 pb-1 border-t border-[var(--m-border)] space-y-2" data-testid={`form-section-${field.id}`}>
            <button type="button" onClick={() => toggleSection(field.id)} className="w-full text-left flex items-center gap-2">
              {collapsedSections[field.id] ? <ChevronRight className="h-4 w-4 text-[#ffd108] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#ffd108] shrink-0" />}
              <h3 className="text-[#ffd108] text-base font-semibold flex-1">{field.label}</h3>
            </button>
            {!collapsedSections[field.id] && field.description && <p className="text-[var(--m-text-secondary)] text-sm whitespace-pre-line">{field.description}</p>}
            {!collapsedSections[field.id] && field.image_url && (
              <img src={field.image_url} alt={field.label} className="w-full rounded-lg border border-[var(--m-border)]" data-testid={`form-section-image-${field.id}`} />
            )}
          </div>
        );

      case 'multi_select':
        return (
          <div key={field.id} className="space-y-2" data-testid={`form-multiselect-${field.id}`}>
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {field.options?.map((option) => {
                const selected = Array.isArray(responses[field.id]) && responses[field.id].includes(option);
                return (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${field.id}-${option}`}
                      checked={selected}
                      onCheckedChange={() => toggleMultiSelect(field.id, option)}
                      data-testid={`multiselect-${field.id}-${option}`}
                    />
                    <Label htmlFor={`${field.id}-${option}`} className="text-[var(--m-text)] cursor-pointer text-sm">{option}</Label>
                  </div>
                );
              })}
            </div>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'repeating_group': {
        const rows = Array.isArray(responses[field.id]) ? responses[field.id] : [];
        const cols = field.columns || [];
        return (
          <div key={field.id} className="space-y-2" data-testid={`form-repeating-${field.id}`}>
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {rows.map((row, rIdx) => (
              <div key={rIdx} className="p-3 rounded-lg bg-[var(--m-bg)] border border-[var(--m-border)] space-y-2 relative" data-testid={`repeating-row-${field.id}-${rIdx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--m-text-secondary)] text-xs">Row {rIdx + 1}</span>
                  <button type="button" onClick={() => removeRepeatingRow(field.id, rIdx)} className="text-red-400 text-xs" data-testid={`remove-row-${field.id}-${rIdx}`}>Remove</button>
                </div>
                {cols.map((col) => (
                  <div key={col.id} className="space-y-1">
                    <Label className="text-[var(--m-text-body)] text-xs">{col.label}</Label>
                    {col.type === 'select' ? (
                      <Select value={row[col.id] || ''} onValueChange={(v) => updateRepeatingRow(field.id, rIdx, col.id, v)}>
                        <SelectTrigger className="bg-[#111] border-[var(--m-border-light)] text-[var(--m-text)] h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {(col.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                        value={row[col.id] || ''}
                        onChange={(e) => updateRepeatingRow(field.id, rIdx, col.id, e.target.value)}
                        className="bg-[#111] border-[var(--m-border-light)] text-[var(--m-text)] h-9"
                        data-testid={`repeating-cell-${field.id}-${rIdx}-${col.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className="w-full border-[var(--m-border-light)] text-[var(--m-text)]"
              onClick={() => addRepeatingRow(field.id, cols)}
              data-testid={`add-row-${field.id}`}
            >
              + {field.add_button_label || 'Add Row'}
            </Button>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );
      }

      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={responses[field.id] || false}
              onCheckedChange={(checked) => handleResponseChange(field.id, checked)}
            />
            <Label htmlFor={field.id} className="text-[var(--m-text)] cursor-pointer">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <DatePicker
              value={responses[field.id] || ''}
              onChange={(d) => handleResponseChange(field.id, d)}
              className={`w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <WheelTimePicker
              value={responses[field.id] || ''}
              onChange={(v) => handleResponseChange(field.id, v)}
              minuteStep={5}
              className={`w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'yes_no':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleResponseChange(field.id, true)}
                className={`flex-1 h-11 rounded-lg border font-medium transition-colors ${responses[field.id] === true ? 'bg-green-500 border-green-500 text-black' : 'bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]'}`}
                data-testid={`yesno-yes-${field.id}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => handleResponseChange(field.id, false)}
                className={`flex-1 h-11 rounded-lg border font-medium transition-colors ${responses[field.id] === false ? 'bg-red-500 border-red-500 text-[var(--m-text)]' : 'bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]'}`}
                data-testid={`yesno-no-${field.id}`}
              >
                No
              </button>
            </div>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'photo':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {responses[field.id] ? (
              <div className="relative w-24 h-24">
                <img
                  src={responses[field.id]}
                  alt={field.label}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => handleResponseChange(field.id, '')}
                  className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                >
                  <X className="h-3 w-3 text-[var(--m-text)]" />
                </button>
              </div>
            ) : (
              <label className={`w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-[#ffd108] transition-colors ${hasError ? 'border-red-500' : 'border-[var(--m-border-light)]'}`}>
                <Camera className="h-6 w-6 text-[var(--m-text-secondary)]" />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFieldPhotoCapture(field.id, e)}
                  className="hidden"
                  data-testid={`photo-input-${field.id}`}
                />
              </label>
            )}
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'signature':
        return (
          <div key={field.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[var(--m-text)]">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <button
                type="button"
                onClick={() => { signatureRefs.current[field.id]?.clear(); handleResponseChange(field.id, ''); }}
                className="text-xs text-[var(--m-text-secondary)] hover:text-[var(--m-text)] flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Clear
              </button>
            </div>
            <div className={`bg-white rounded-lg overflow-hidden border ${hasError ? 'border-red-500' : 'border-transparent'}`}>
              <SignatureCanvas
                ref={(el) => { signatureRefs.current[field.id] = el; }}
                canvasProps={{ className: 'w-full h-32', 'data-testid': `signature-${field.id}` }}
                backgroundColor="white"
                onEnd={() => handleResponseChange(field.id, signatureRefs.current[field.id]?.toDataURL() || '')}
              />
            </div>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'multi_photo': {
        const imgs = Array.isArray(responses[field.id]) ? responses[field.id] : [];
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[var(--m-text)]">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex flex-wrap gap-2">
              {imgs.map((photo, index) => (
                <div key={`${field.id}-${index}`} className="relative w-20 h-20">
                  <img
                    src={photo}
                    alt={`${field.label} ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeMultiPhoto(field.id, index)}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                  >
                    <X className="h-3 w-3 text-[var(--m-text)]" />
                  </button>
                </div>
              ))}
              <label className={`w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-[#ffd108] transition-colors ${hasError ? 'border-red-500' : 'border-[var(--m-border-light)]'}`}>
                <Camera className="h-6 w-6 text-[var(--m-text-secondary)]" />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={(e) => handleMultiPhotoCapture(field.id, e)}
                  className="hidden"
                  data-testid={`multiphoto-input-${field.id}`}
                />
              </label>
            </div>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <div className="text-center text-[var(--m-text-muted)]">
          <AlertCircle className="h-12 w-12 mx-auto mb-3" />
          <p>Form not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/mobile/forms')} className="text-[var(--m-text-secondary)] p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-[var(--m-text)] font-semibold">{template.name}</h1>
            {job && (
              <p className="text-xs text-[var(--m-text-secondary)]">
                {job.customer_name} - {job.site_name || 'Job'}
              </p>
            )}
          </div>
        </div>
        <button 
          onClick={() => navigate('/mobile/dashboard')} 
          className="text-[var(--m-text-secondary)] p-1"
          data-testid="home-btn"
        >
          <Home className="h-5 w-5" />
        </button>
      </div>

      {/* Form Content */}
      <div className="p-4 space-y-6 pb-6">
        {/* Form Description */}
        {template.description && (
          <p className="text-[var(--m-text-secondary)] text-sm">{template.description}</p>
        )}

        {/* Approval gate notice (Phase 4) */}
        {template.requires_approval && (
          <div className="rounded-lg border border-amber-600/50 bg-amber-950/30 p-3 flex items-start gap-2" data-testid="form-approval-notice">
            <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200">
              {template.approval_message || 'This form may require supervisor approval before work can proceed.'}
            </p>
          </div>
        )}

        {/* Form Fields (conditional fields hidden until their trigger matches) */}
        <div className="space-y-4">
          {(() => {
            const vis = template.fields?.filter(f => fieldVisible(f, responses)) || [];
            // Map each field to the section it belongs to, in order.
            let curSection = null;
            const sectionOf = {};
            vis.forEach(f => {
              const ft = f.field_type || f.type;
              if (ft === 'section' || ft === 'section_header') { curSection = f.id; }
              else { sectionOf[f.id] = curSection; }
            });
            return vis
              .filter(f => {
                const sec = sectionOf[f.id];
                return !(sec && collapsedSections[sec]);
              })
              .map(field => renderField(field));
          })()}
        </div>

        {/* Submit Action — inline at end of form */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-12 bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold text-base"
          data-testid="submit-form-btn"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {submitting ? 'Submitting...' : 'Submit Form'}
        </Button>
      </div>
    </div>
  );
};

export default MobileFormSubmission;
